import React, { useContext, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { SubscriptionContext } from "../SubscriptionContext";
import { SubscriptionPaymentMode } from "src/constants/userDetails";

// global components
import {
    FlexContainer,
    Button,
    BusinessPortalHeader,
    PortalActionBar,
    PortalCaption,
    PortalMetricLabel,
    PortalPanelHeader,
    PortalSectionTitle,
    SplitLayoutRightImage,
    portalColors
} from "src/components";

// assets
import GreenCheck from "src/img/new/green-check.svg";
import RightImage from "src/img/subscription/2.jpg";

const TypeSelection = styled(FlexContainer)`
    width: 18.6875rem;
    height: 21.0625rem;
    border: 1px solid ${p => p.active ? portalColors.text : portalColors.border};
    border-radius: var(--calendar-radius-card, 12px);
    margin-top: 1.875rem;
    margin-right: 1.9375rem;
    background: ${portalColors.surfaceSoft};
    color: ${portalColors.text};
    box-shadow: ${portalColors.shadow};
    box-sizing: border-box;
    overflow: hidden;
    transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease, background 0.16s ease;
    &:hover{
        border-color: ${portalColors.borderStrong};
        transform: translateY(-1px);
        cursor: pointer;
    };
    ${(props) =>
        props.active &&
        css`
          background: ${portalColors.text};
          color: var(--calendar-bg, #FFFFFF) !important;
          &:hover {
            border-color: ${portalColors.text};
        }
    `};
    ${(props) =>
        props.disabled &&
        css`
            cursor: not-allowed !important;
            color: ${portalColors.textSoft} !important;
            opacity: 0.72;
            &:hover {
                transform: none;
            };
    `};
`;

const CardHeadingWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    height: 2.1875rem;
    box-sizing: border-box;
    align-items: flex-end;
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1.375rem;
`;

const Price = styled.div`
    font-size: 3.75rem;
    font-weight: 700;
    margin-bottom: 8px;
`;

const Description = styled.div`
    font-size: 1rem;
    font-weight: 400;
`;

const CardAction = styled.div`
    background: ${p => p.active ? "transparent" : portalColors.surfaceTertiary};
    display: flex;
    justify-content: center;
    align-items: center;
    height: 5.3125rem;
    border-top: 1px solid ${p => p.active ? "rgba(255,255,255,0.22)" : portalColors.border};
`;

const Image = styled.img`
    position: relative;
    object-fit: contain;
`;

const SelectionButton = styled(Button)`
    background-color: ${portalColors.surface};
    color: ${portalColors.text};
    font-size: 0.935rem;
    border: 1px solid ${portalColors.border};
    border-radius: var(--calendar-radius-control, 8px) !important;
    font-family: Manrope;
    font-weight: 700;
    height: 2.3125rem;
    width: 8.75rem;
    line-height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    ::before {
        content: "Select";
    }

    ${(props) =>
        props.active &&
        css`
          border: 1px solid transparent !important;
          color: ${portalColors.text} !important;
          cursor: none;
          pointer-events: none;

          ::before {
            content: "Selected";
          }
    `};

    ${(props) =>
        props.disabled &&
        css`
            pointer-events: none;
            ::before {
                content: "Not Available" !important;
            }
    `};
`;

const StyledButton = styled(Button)`
    font-family: Manrope;
    border-radius: var(--calendar-radius-control, 8px) !important;
    width: 8.75rem;
    height: 2.625rem;
    line-height: 100%;
    font-weight: 700;
    font-size: 0.875rem;
`;

const Currency = styled.span`
    margin-left: 0.47rem;
    font-size: 1rem;
    font-weight: 700;
`;

const PlanGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
`;

const SubscriptionType = () => {
    const context = useContext(SubscriptionContext);
    const { costing, updateCompanyValueAndPossiblyGoNext, isPurchasingMoreUsers } = context;
    const [paymentMode, setPaymentMode] = useState(SubscriptionPaymentMode.Upfront);
    const [matches, setMatches] = useState(window.matchMedia("(max-width: 1510px)").matches);

    useEffect(() => {
        const mediaQuery = window.matchMedia("(max-width: 1510px)");
        function handleMediaQueryChange(e) {
            setMatches(e.matches);
        }

        mediaQuery.addEventListener("change", handleMediaQueryChange);

        return () => mediaQuery.removeEventListener("change", handleMediaQueryChange);
    }, []);

    const handleTypeSelection = (value) => {
        setPaymentMode(value);
    };

    const proceed = (e) => {
        e.preventDefault();

        updateCompanyValueAndPossiblyGoNext("paymentMode", paymentMode, true);
    };

    const isPlanSelected = plan => plan.paymentMode === paymentMode;

    return (
        <React.Fragment>
            <BusinessPortalHeader
                title={!isPurchasingMoreUsers ? "Subscription Type: Step 2" : "Purchase More User Licenses"}
                description={!isPurchasingMoreUsers ?
                    "Select what type of subscription you would like for your business and app user licenses."
                    : "Select what type of subscription you would like your additional user licenses to use."
                }
            />
            <SplitLayoutRightImage image={RightImage}
                width="100%"
                imageVisibility={matches ? "hidden" : "visible"}
            >
                <PortalPanelHeader withDivider>
                    <div>
                        <PortalSectionTitle>Subscription Type</PortalSectionTitle>
                        <PortalCaption margin="0.25rem 0 0">
                            Select the billing option for these user licenses.
                        </PortalCaption>
                    </div>
                </PortalPanelHeader>
                <PlanGrid>
                    { costing.plans.map((plan) => (
                        <TypeSelection key={"TypeSelection_" + plan.name} active={isPlanSelected(plan)} onClick={() => handleTypeSelection(plan.name)}>
                            <FlexContainer padding="1.25rem" height="15.75rem">
                                <CardHeadingWrapper>
                                    {plan.name}
                                    {isPlanSelected(plan) && <Image src={GreenCheck} />}
                                </CardHeadingWrapper>
                                <Price>
                                    ${(plan.periodNetAmountCents / 100).toFixed(2)}<Currency>{plan.currency}</Currency>
                                </Price>
                                <Description active={isPlanSelected(plan)}>
                                    Per user, per {plan.getDurationUnitLowercase()} <br />
                                    {plan.getPaymentDescription()}
                                </Description>
                            </FlexContainer>
                            <CardAction active={isPlanSelected(plan)}>
                                <SelectionButton active={isPlanSelected(plan)} />
                            </CardAction>
                        </TypeSelection>
                    )) }
                </PlanGrid>

                <PortalMetricLabel color={portalColors.mutedText}>
                    * Subscriptions are 12 months fixed term - Excluding government charges<br />
                    * Subscription automatically renews unless canceled 24-hours before the end of the current term.
                </PortalMetricLabel>

                <PortalActionBar
                    justifyContent="flex-start"
                    padding="1rem 0 0"
                    border="transparent"
                    shadow="none"
                    radius="0"
                >
                    <StyledButton
                        border={`1px solid ${portalColors.border}`}
                        color={portalColors.text}
                        backgroundColor={portalColors.surface}
                        onClick={() => context.backToPrevious()}
                    >
                        Back
                    </StyledButton>
                    <StyledButton
                        color="var(--calendar-bg, #FFFFFF)"
                        backgroundColor={portalColors.text}
                        border={`1px solid ${portalColors.text}`}
                        onClick={(e) => proceed(e)}
                    >
                        Next
                    </StyledButton>
                </PortalActionBar>
            </SplitLayoutRightImage>
        </React.Fragment >
    );
};

export default SubscriptionType;
